import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useConvexAuth } from 'convex/react'
import { ArrowLeftRight } from 'lucide-react'
import { type CSSProperties, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { SettingsGroup } from '@/components/settings/primitives'
import { ACCENT_COLORS, refreshFavicon, useAccentTheme } from '@/hooks/use-accent-theme'
import {
  COACH_GREEN,
  coachingRedirect,
  isPersonalOnly,
  switchMode,
  useIsCoach,
  useIsCoaching,
} from '@/lib/coaching-mode'
import { useUserMode } from '@/store/user-mode.store'

// Coaching mode swaps the accent site-wide (html.coaching in index.css), the toolbar colour and the
// tab icon, and keeps personal pages out of it (the goals list becomes Client plans). A push opened onto a personal page (?pushed=1,
// added by sw.js) belongs to the personal side, so it switches back instead.
export function CoachingAccent() {
  const { isAuthenticated } = useConvexAuth()
  const mode = useUserMode((s) => s.mode)
  const setMode = useUserMode((s) => s.setMode)
  const isCoach = useIsCoach()
  const coaching = useIsCoaching()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const navigate = useNavigate()

  useEffect(() => {
    if (mode === 'coaching' && isAuthenticated && isCoach === false) setMode('personal')
  }, [mode, isAuthenticated, isCoach, setMode])

  useEffect(() => {
    if (!coaching || !isPersonalOnly(pathname)) return
    if (new URLSearchParams(window.location.search).has('pushed')) setMode('personal')
    else void navigate({ to: coachingRedirect(pathname), replace: true })
  }, [coaching, pathname, navigate, setMode])

  useEffect(() => {
    document.documentElement.classList.toggle('coaching', coaching)
    refreshFavicon()
    if (!coaching) return
    const metas = Array.from(document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'))
    const prev = metas.map((m) => m.content)
    for (const m of metas) m.content = COACH_GREEN
    return () => metas.forEach((m, i) => (m.content = prev[i] ?? m.content))
  }, [coaching])

  return null
}

/** Filled in the colour of the side it switches to: coach green, or the user's own accent. */
export function useSwitchModeStyle(): { className: string; style?: CSSProperties } {
  const coaching = useIsCoaching()
  const { accent } = useAccentTheme()
  if (!coaching)
    return { className: 'bg-coach text-white hover:bg-coach/90 focus:bg-coach/90 focus:text-white' }
  if (accent === 'mono')
    return {
      className:
        'bg-foreground text-background hover:opacity-90 focus:bg-foreground focus:text-background',
    }
  return {
    className: 'text-[#0a0a0a] hover:opacity-90 focus:text-[#0a0a0a]',
    style: { background: ACCENT_COLORS[accent] },
  }
}

export function useSwitchMode() {
  const coaching = useIsCoaching()
  const navigate = useNavigate()
  return () => switchMode(coaching ? 'personal' : 'coaching', navigate)
}

export function SwitchModeButton({
  onSwitch,
  className,
}: {
  onSwitch?: () => void
  className?: string
}) {
  const { t } = useTranslation()
  const isCoach = useIsCoach()
  const coaching = useIsCoaching()
  const look = useSwitchModeStyle()
  const toggle = useSwitchMode()
  if (!isCoach) return null
  return (
    <button
      type="button"
      onClick={() => {
        onSwitch?.()
        toggle()
      }}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-[background-color,opacity]',
        look.className,
        className,
      )}
      style={look.style}
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-xl bg-white/20">
        <ArrowLeftRight className="size-4" strokeWidth={2.25} />
      </span>
      <span className="flex-1 text-sm font-semibold">
        {coaching ? t('coachingMode.switchToPersonal') : t('coachingMode.switchToCoaching')}
      </span>
    </button>
  )
}

export function CoachingBadge({ className }: { className?: string }) {
  const { t } = useTranslation()
  if (!useIsCoaching()) return null
  return (
    <span
      className={cn(
        'shrink-0 rounded-full bg-coach/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-coach-text',
        className,
      )}
    >
      {t('coachingMode.badge')}
    </span>
  )
}

export function CoachingModeSettings() {
  const { t } = useTranslation()
  const isCoach = useIsCoach()
  const coaching = useIsCoaching()
  if (!isCoach) return null
  return (
    <SettingsGroup>
      {/* Stacked on phones: the wide switch button doesn't fit beside the description. */}
      <div className="flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:gap-4">
        <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground sm:flex">
          <ArrowLeftRight className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{t('coachingMode.settingsTitle')}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {coaching ? t('coachingMode.settingsOn') : t('coachingMode.settingsOff')}
          </p>
        </div>
        <SwitchModeButton className="w-full shrink-0 rounded-full py-1.5 pr-4 pl-1.5 sm:w-auto" />
      </div>
    </SettingsGroup>
  )
}
